import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import { useIntegrationsContext } from '@insights_module/context/IntegrationsContext';
import { CreateTagForm } from '@insights_module/components/integrations/CreateTagForm';
import { CloseIcon } from '@icons/CloseIcon';
import { Button } from '@components/Button';
import { Modal } from '@components/Modal';
import { SegmentCreatorProvider } from '@insights_module/context/SegmentCreatorContext';
import { IntegrationType } from '@insights_module/types/Integrations';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';

const Container = tw.div`relative w-full max-w-[1200px] lg:min-w-[1200px] border border-solid border-gray-1 rounded-lg mx-auto`;
const Header = tw.div`w-full bg-neutral-background px-6 py-2 border-b border-solid border-gray-1 flex items-center justify-between rounded-t-lg`;
const Title = tw.span`text-sm text-text-and-icon text-left font-semibold`;
const ModalButton = tw(
  Button.Primary
)`text-xs leading-none p-0 w-auto h-[28px] border-none shadow-none align-middle`;

export const CreateTagModal = ({ type }: { type: IntegrationType }): JSX.Element | null => {
  const { isEnabled: isFlightpathEnabled } = useFeatureFlag(
    FEATURE_FLAG.MEGAPHONE_FLIGHTPATH_INTEGRATION
  );
  const { t } = useTranslation();
  const { isCreateTagModalOpen, tagToDuplicate, setIsCreateTagModalOpen, setTagToDuplicate } =
    useIntegrationsContext();

  const handleCloseModal = () => {
    setIsCreateTagModalOpen(false);
    setTagToDuplicate(null);
  };

  if (!isCreateTagModalOpen) {
    return null;
  }

  return (
    <Modal onClose={() => handleCloseModal()}>
      <Container
        css={[
          type === 'megaphone' &&
            isFlightpathEnabled &&
            tw`md:w-[calc(100% - 76px)] max-w-[1600px]`,
        ]}
        tw='md:w-[calc(100% - 76px)]'
        data-testid='create-tag-modal'>
        <Header>
          <Title>
            {tagToDuplicate
              ? t('insights.integrations.megaphone.duplicate_tag_modal.title')
              : t('insights.integrations.megaphone.create_tag_modal.title')}
          </Title>
          <ModalButton
            tw='bg-transparent border-none w-[28px]'
            onClick={handleCloseModal}
            data-testid='close-modal-button'>
            <CloseIcon tw='fill-text-and-icon' />
          </ModalButton>
        </Header>

        <SegmentCreatorProvider>
          <CreateTagForm type={type} />
        </SegmentCreatorProvider>
      </Container>
    </Modal>
  );
};
